import React from "react";
import { Timer, CheckCircle2, ChevronLeft, ChevronRight, Flame, Trophy, Dumbbell } from "lucide-react";

const Label = ({ children }) => <div className="text-[10px] uppercase tracking-wider text-slate-400">{children}</div>;
const Card = ({ children, className = "" }) => <div className={`rounded-2xl border border-slate-800 bg-slate-900/80 p-3 ${className}`}>{children}</div>;

const SETS = [["1", "40", "12", true, true], ["2", "60", "10", true], ["3", "60", "8", true], ["4", "62.5", "—"]];

export function TrainScreen({ id }) {
  return (
    <div>
      <div className="flex items-start justify-between">
        <div>
          <Label>{id ? "Sesi aktif" : "Active session"}</Label>
          <div className="font-display text-lg font-bold text-white">Push Day</div>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-400 tabular-nums"><Timer className="h-3 w-3" /> 32:14</span>
      </div>
      <div className="mt-3 flex justify-between text-[10px] text-slate-400"><span>{id ? "Progres sesi" : "Session progress"}</span><span>6/12 set</span></div>
      <div className="mt-1 h-1.5 rounded-full bg-slate-800"><div className="h-full w-1/2 rounded-full bg-emerald-400" /></div>
      <Card className="mt-3">
        <div className="text-xs font-semibold text-white">Bench Press</div>
        <div className="text-[10px] text-slate-400">{id ? "Dada · Barbell" : "Chest · Barbell"}</div>
        <div className="mt-2 grid grid-cols-4 text-[9px] uppercase tracking-wider text-slate-500"><span>Set</span><span>kg</span><span>Reps</span><span className="text-right">OK</span></div>
        {SETS.map(([n, kg, reps, done, warm]) => (
          <div key={n} className={`mt-1 grid grid-cols-4 items-center rounded-lg px-1 py-1 text-[11px] tabular-nums ${done ? "text-slate-300" : "bg-emerald-400/10 text-white"}`}>
            <span>{warm ? <span className="rounded bg-amber-400/15 px-1 text-[9px] font-bold text-amber-300">W</span> : n}</span>
            <span>{kg}</span><span>{reps}</span>
            <span className="flex justify-end">{done ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> : <span className="h-3.5 w-3.5 rounded-full border border-emerald-400/60" />}</span>
          </div>
        ))}
      </Card>
      <div className="mt-3 flex items-center justify-between rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-3 py-2">
        <span className="text-[11px] text-emerald-300">{id ? "Istirahat" : "Rest"}</span>
        <span className="font-display text-sm font-bold text-white tabular-nums">01:30</span>
      </div>
      <Card className="mt-3 flex items-center justify-between py-2.5">
        <span className="text-xs font-semibold text-white">Incline DB Press</span>
        <span className="text-[10px] text-slate-400">0/3 set</span>
      </Card>
      <div className="mt-3 rounded-xl bg-emerald-400 py-2 text-center text-xs font-bold text-slate-950">{id ? "Akhiri Sesi" : "End Session"}</div>
    </div>
  );
}

const WORKOUT_DAYS = new Set([1, 3, 6, 8, 10, 13, 15, 17, 20]);
const CHECKIN_DAYS = new Set([1, 2, 3, 5, 6, 8, 9, 10, 12, 13, 15, 16, 17, 19, 20]);

function MiniCalendar({ id }) {
  const week = id ? ["S", "S", "R", "K", "J", "S", "M"] : ["M", "T", "W", "T", "F", "S", "S"];
  const cells = [...Array(3).fill(null), ...Array.from({ length: 31 }, (_, i) => i + 1)];
  return (
    <div className="mt-2 grid grid-cols-7 gap-y-1 text-center text-[10px]">
      {week.map((d, i) => <span key={i} className="text-slate-500">{d}</span>)}
      {cells.map((d, i) => (
        <span key={i} className={`relative mx-auto grid h-6 w-6 place-items-center rounded-lg tabular-nums ${d === 15 ? "bg-emerald-400 font-bold text-slate-950" : d && d <= 20 ? "text-slate-200" : "text-slate-600"}`}>
          {d}
          {d && d !== 15 && WORKOUT_DAYS.has(d) && <Flame className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 text-orange-400" />}
          {d && d !== 15 && CHECKIN_DAYS.has(d) && <span className="absolute bottom-0 h-1 w-1 rounded-full bg-emerald-400" />}
        </span>
      ))}
    </div>
  );
}

export function HistoryScreen({ id }) {
  return (
    <div>
      <div className="font-display text-lg font-bold text-white">{id ? "Riwayat" : "History"}</div>
      <div className="mt-2 flex items-center justify-between text-xs font-semibold text-white">
        <ChevronLeft className="h-4 w-4 text-slate-500" /> {id ? "Oktober 2026" : "October 2026"} <ChevronRight className="h-4 w-4 text-slate-500" />
      </div>
      <div className="mt-2 grid grid-cols-3 gap-1.5 text-center">
        {[["2.140", id ? "kkal" : "kcal"], ["9", id ? "sesi" : "sessions"], ["15", "check-in"]].map(([v, l]) => (
          <div key={l} className="rounded-xl bg-slate-900/80 py-1.5"><div className="text-xs font-bold text-white tabular-nums">{v}</div><div className="text-[9px] text-slate-400">{l}</div></div>
        ))}
      </div>
      <MiniCalendar id={id} />
      <Card className="mt-3">
        <Label>{id ? "Kamis, 15 Okt" : "Thu, Oct 15"}</Label>
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-emerald-300"><CheckCircle2 className="h-3.5 w-3.5" /> Check-in · Gym</div>
        <div className="mt-2 flex items-center gap-2">
          <div className="grid h-7 w-7 place-items-center rounded-lg bg-orange-400/10 text-orange-400"><Flame className="h-3.5 w-3.5" /></div>
          <div><div className="text-xs font-semibold text-white">Upper Body</div><div className="text-[10px] text-slate-400">48 {id ? "mnt" : "min"} · ~312 {id ? "kkal" : "kcal"}</div></div>
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {["Bench Press", "Barbell Row", "OHP"].map((e) => <span key={e} className="rounded-full bg-slate-800 px-2 py-0.5 text-[9px] text-slate-300">{e}</span>)}
        </div>
      </Card>
    </div>
  );
}

const HEAT = Array.from({ length: 49 }, (_, i) => [0, 3, 1, 2, 0, 3, 2, 1, 3, 0, 2][(i * 7 + (i % 5)) % 11]);
const HEAT_CLS = ["bg-slate-800", "bg-emerald-400/30", "bg-emerald-400/60", "bg-emerald-400"];
const E1RM = [62, 64, 63, 67, 69, 68, 72, 75];

function E1rmChart() {
  const pts = E1RM.map((v, i) => `${(i / (E1RM.length - 1)) * 100},${40 - ((v - 60) / 16) * 36}`).join(" ");
  return (
    <svg viewBox="0 0 100 42" className="mt-2 h-14 w-full" preserveAspectRatio="none">
      <polygon points={`0,42 ${pts} 100,42`} fill="rgb(52 211 153 / 0.15)" />
      <polyline points={pts} fill="none" stroke="rgb(52 211 153)" strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export function ProgressScreen({ id }) {
  return (
    <div>
      <div className="font-display text-lg font-bold text-white">{id ? "Progres" : "Progress"}</div>
      <Card className="mt-3">
        <Label>{id ? "Konsistensi 7 minggu" : "7-week consistency"}</Label>
        <div className="mt-2 grid grid-cols-7 gap-1">
          {HEAT.map((h, i) => <span key={i} className={`aspect-square rounded-[3px] ${HEAT_CLS[h]}`} />)}
        </div>
      </Card>
      <Card className="mt-3">
        <div className="flex items-end justify-between">
          <div><Label>Bench Press · e1RM</Label><div className="font-display text-lg font-bold text-white tabular-nums">75 kg</div></div>
          <span className="text-[11px] font-semibold text-emerald-400">+13 kg</span>
        </div>
        <E1rmChart />
      </Card>
      <div className="mt-3 space-y-2">
        {[["Barbell Squat", "100 kg × 5"], ["Deadlift", "120 kg × 3"]].map(([n, v]) => (
          <div key={n} className="flex items-center justify-between rounded-xl bg-slate-900/80 px-3 py-2">
            <div className="flex items-center gap-2"><Trophy className="h-3.5 w-3.5 text-amber-300" /><span className="text-xs font-semibold text-white">{n}</span></div>
            <span className="text-[10px] text-slate-400 tabular-nums">PR · {v}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-1.5 rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-3 py-2 text-[10px] text-emerald-300">
        <Dumbbell className="h-3.5 w-3.5" /> {id ? "Saran: naikkan beban +2.5 kg" : "Tip: add +2.5 kg next session"}
      </div>
    </div>
  );
}
